import { useCallback, useMemo } from 'react';
import { useNavigate, NavigateOptions } from 'react-router-dom';
import useQueryParams from './useQueryParams';
import useUserSession from './useUserSession';
import { SELECT_MODULE } from '../shared/Routes';

export const useBatchId = () => {
	const navigate = useNavigate();
	const queryParams = useQueryParams();
	const { currentActiveBatch } = useUserSession();

	const batchId = useMemo(() => {
		const id = currentActiveBatch?.id;
		const batchIdParam =
			id !== undefined && id !== null ? Number(id) : undefined;

		if (typeof batchIdParam !== 'number' || isNaN(batchIdParam)) {
			return -1;
		}

		return batchIdParam;
	}, [currentActiveBatch]);

	const getRouteWithParams = useCallback(
		(route: string, removeSrcmParam?: boolean) => {
			if (route === SELECT_MODULE) {
				return route;
			}
			if (removeSrcmParam) {
				queryParams.delete('functionarySrcmIdParam');
			}
			return `${route}?${queryParams.toString()}`;
		},
		[queryParams]
	);

	const navigateToRoute = useCallback(
		(route: string, params?: NavigateOptions) => {
			navigate(`${getRouteWithParams(route)}`, params);
		},
		[getRouteWithParams, navigate]
	);

	const removeQueryParamFromUrl = useCallback(() => {
		const params = new URLSearchParams(location.search);
		const applicationId = params.get('applicationId');
		if (applicationId) {
			params.delete('applicationId');
			const query = params.toString();
			navigate(query ? `${location.pathname}?${query}` : location.pathname, {
				replace: true,
			});
		}
		queryParams.delete('regionId');
	}, [navigate, queryParams]);

	const preceptorBatchId = useMemo(() => {
		const batchIdParam = Number(queryParams.get('preceptorBatchId'));
		if (typeof batchIdParam !== 'number') {
			return -1;
		}
		return batchIdParam;
	}, [queryParams]);

	const navigateWithPreceptorBatchId = useCallback(
		(
			route: string,
			preceptorBatchIdParam?: number,
			params?: NavigateOptions
		) => {
			const batchIdToUse = preceptorBatchIdParam
				? `${preceptorBatchIdParam}`
				: preceptorBatchId;
			queryParams.set('preceptorBatchId', `${batchIdToUse}`);
			navigateToRoute(route, params);
		},
		[preceptorBatchId, navigateToRoute, queryParams]
	);

	const ctdCategoryKey = useMemo(() => {
		const categoryParam = queryParams.get('category');
		if (typeof categoryParam !== 'string') {
			return -1;
		}
		return categoryParam;
	}, [queryParams]);

	const navigateWithCTDCategory = useCallback(
		(route: string, categoryParam?: string, params?: NavigateOptions) => {
			const categoryToUse = categoryParam ? `${categoryParam}` : ctdCategoryKey;
			queryParams.set('category', `${categoryToUse}`);
			navigateToRoute(route, params);
		},
		[ctdCategoryKey, navigateToRoute, queryParams]
	);

	const proposerLoadRegion = useMemo(() => {
		const categoryParam = queryParams.get('regionId');
		if (typeof categoryParam !== 'number') {
			return -1;
		}
		return categoryParam;
	}, [queryParams]);

	const candidateLoadZone = useMemo(() => {
		const zoneParam = Number(queryParams.get('zoneId'));
		const applicationIdParam = Number(queryParams.get('applicationId'));

		if (isNaN(zoneParam) && isNaN(applicationIdParam)) {
			return -1;
		}

		return !isNaN(zoneParam) ? zoneParam : applicationIdParam;
	}, [queryParams]);

	const navigatewithRegionId = useCallback(
		(route: string, regionIdParams?: number, params?: NavigateOptions) => {
			const regionToLoad = regionIdParams
				? `${regionIdParams}`
				: proposerLoadRegion;
			queryParams.set('regionId', `${regionToLoad}`);
			navigateToRoute(route, params);
		},
		[proposerLoadRegion, navigateToRoute, queryParams]
	);

	const navigatewithZoneId = useCallback(
		(
			route: string,
			ZoneIdParams?: number,
			applicationIdParams?: number,
			params?: NavigateOptions
		) => {
			const zoneToLoad = ZoneIdParams ? `${ZoneIdParams}` : candidateLoadZone;
			queryParams.set('zoneId', `${zoneToLoad}`);
			if (applicationIdParams) {
				queryParams.set('applicationId', `${applicationIdParams}`);
			}
			navigateToRoute(route, params);
		},
		[candidateLoadZone, navigateToRoute, queryParams]
	);

	const navigatewithoutZoneId = useCallback(
		(route: string, params?: NavigateOptions) => {
			queryParams.delete('zoneId');
			queryParams.delete('applicationId');
			navigate(`${getRouteWithParams(route)}`, params);
		},
		[getRouteWithParams, navigate, queryParams]
	);

	return {
		batchId,
		preceptorBatchId,
		navigateWithPreceptorBatchId,
		navigateWithCTDCategory,
		queryParams,
		navigatewithRegionId,
		navigatewithZoneId,
		navigatewithoutZoneId,
		navigateToRoute,
		getRouteWithParams,
		removeQueryParamFromUrl,
	};
};
